import React, { useState, useEffect } from 'react';
import { collection, getDocs, addDoc, updateDoc, doc, setDoc, query, where } from 'firebase/firestore';
import { db } from '../../firebaseConfig';

const defaultItemType = {
  id: "",
  name: "",
  description: "",
  categoryId: "",
  subcategoryId: "",
  active: true,
  addedDate: ""
};

export default function ItemTypes() {
  const [itemTypes, setItemTypes] = useState<any[]>([]);
  const [categories, setCategories] = useState<any[]>([]);
  const [subcategories, setSubcategories] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [editingItemType, setEditingItemType] = useState<any>(null);
  const [itemType, setItemType] = useState<any>(defaultItemType);
  const [saving, setSaving] = useState(false);
  const [itemTypeSearchTerm, setItemTypeSearchTerm] = useState("");
  const [itemTypeActiveFilter, setItemTypeActiveFilter] = useState("");
  const [itemTypeCategoryFilter, setItemTypeCategoryFilter] = useState("");
  const [itemTypeSubcategoryFilter, setItemTypeSubcategoryFilter] = useState("");

  useEffect(() => {
    const fetchData = async () => {
      try {
        // Fetch categories and subcategories
        const [categoriesSnap, subcategoriesSnap] = await Promise.all([
          getDocs(collection(db, "categories")),
          getDocs(collection(db, "subcategories"))
        ]);
        
        const categoriesData = categoriesSnap.docs.map(doc => ({
          ...doc.data(),
          id: doc.id
        }));
        
        const subcategoriesData = subcategoriesSnap.docs.map(doc => ({
          ...doc.data(),
          id: doc.id
        }));
        
        // Deduplicate categories by name (keep first occurrence) and sort alphabetically
        const uniqueCategoriesData = Array.from(
          new Map(categoriesData.map((cat: any) => [cat.name, cat])).values()
        ).sort((a: any, b: any) => (a.name || '').localeCompare(b.name || ''));
        
        // Deduplicate subcategories by ID (not name, since same name can exist for different categories)
        // Use a Map with id as key to ensure uniqueness
        const uniqueSubcategoriesData = Array.from(
          new Map(subcategoriesData.map((sub: any) => [sub.id, sub])).values()
        ).sort((a: any, b: any) => (a.name || '').localeCompare(b.name || ''));
        
        setCategories(uniqueCategoriesData);
        setSubcategories(uniqueSubcategoriesData);

        // Try to fetch from itemTypes collection (may not exist or have permissions)
        let itemTypesData: any[] = [];
        try {
          const itemTypesSnap = await getDocs(collection(db, "itemTypes"));
          itemTypesData = itemTypesSnap.docs.map(doc => ({
            ...doc.data(),
            id: doc.id
          }));
        } catch (collectionError: any) {
          // Collection doesn't exist or no permissions - that's okay, we'll use tasks
          console.log("ItemTypes collection not accessible, using tasks only:", collectionError.message);
        }

        // Extract item types from tasks (this is the source of truth)
        // Also link them to their category/subcategory based on tasks
        // Note: Item types can be used across different categories/subcategories
        // We'll use the first occurrence we find - users can create additional entries if needed
        const tasksSnap = await getDocs(collection(db, "tasks"));
        const itemTypeToTaskMap = new Map<string, {name: string, category: string, subcategory: string}>();
        tasksSnap.docs.forEach(doc => {
          const task = doc.data();
          if (task.itemType && typeof task.itemType === 'string' && task.itemType.trim()) {
            const itemTypeName = task.itemType.trim();
            // Store first occurrence - category/subcategory is just for filtering when creating tasks
            // Only store if both category and subcategory are present and non-empty
            if (!itemTypeToTaskMap.has(itemTypeName) && 
                task.category && typeof task.category === 'string' && task.category.trim() &&
                task.subcategory && typeof task.subcategory === 'string' && task.subcategory.trim()) {
              itemTypeToTaskMap.set(itemTypeName, {
                name: itemTypeName,
                category: task.category.trim(),
                subcategory: task.subcategory.trim()
              });
            }
          }
        });

        console.log("=== ITEM TYPES DEBUG ===", {
          fromCollection: itemTypesData.length,
          fromTasks: itemTypeToTaskMap.size,
          taskItemTypes: Array.from(itemTypeToTaskMap.keys())
        });

        // Create a map of existing item types from collection
        const itemTypesMap = new Map<string, any>();
        itemTypesData.forEach((it: any) => {
          if (it.name) {
            itemTypesMap.set(it.name, it);
          }
        });

        // Add item types from tasks that aren't in the collection yet (as temporary items)
        // Link them to their category/subcategory
        const tempItemTypes: any[] = [];
        itemTypeToTaskMap.forEach((taskInfo, name) => {
          if (name && !itemTypesMap.has(name)) {
            // Find category by exact name match (case-insensitive, trimmed)
            const category = uniqueCategoriesData.find((cat: any) => {
              const catName = (cat.name || '').trim();
              const taskCatName = (taskInfo.category || '').trim();
              return catName === taskCatName || catName.toLowerCase() === taskCatName.toLowerCase();
            });
            
            // Find subcategory - MUST belong to the found category (no fallback to wrong category)
            let subcategory = null;
            if (category && taskInfo.subcategory) {
              const taskSubName = (taskInfo.subcategory || '').trim();
              // Find subcategory that matches name AND belongs to the category
              subcategory = uniqueSubcategoriesData.find((sub: any) => {
                const subName = (sub.name || '').trim();
                const nameMatches = subName === taskSubName || subName.toLowerCase() === taskSubName.toLowerCase();
                const belongsToCategory = sub.categoryId === category.id;
                return nameMatches && belongsToCategory;
              });
            }
            
            // If category not found or subcategory not found, log for debugging
            if (taskInfo.subcategory && !subcategory) {
              console.warn(`Item Type "${name}": Could not find subcategory "${taskInfo.subcategory}" for category "${taskInfo.category}"`, {
                categoryFound: !!category,
                categoryId: category?.id,
                availableSubcategories: category ? uniqueSubcategoriesData.filter((s: any) => s.categoryId === category.id).map((s: any) => s.name) : []
              });
            }
            
            tempItemTypes.push({
              id: `temp-${name}`,
              name,
              description: "",
              categoryId: category?.id || "",
              subcategoryId: subcategory?.id || "",
              active: true,
              addedDate: new Date().toISOString(),
              fromTasks: true // Flag to indicate this came from tasks
            });
          }
        });

        // Combine collection items with temporary items from tasks
        const allItemTypes = [...itemTypesData, ...tempItemTypes].sort((a, b) => 
          (a.name || '').localeCompare(b.name || '')
        );

        console.log("=== FINAL ITEM TYPES ===", {
          total: allItemTypes.length,
          fromCollection: itemTypesData.length,
          fromTasks: tempItemTypes.length,
          allNames: allItemTypes.map(it => it.name)
        });

        setItemTypes(allItemTypes);
      } catch (error) {
        console.error("Error fetching item types:", error);
        // Even if there's an error, try to show what we can from tasks
        try {
          const tasksSnap = await getDocs(collection(db, "tasks"));
          const uniqueItemTypes = new Set<string>();
          tasksSnap.docs.forEach(doc => {
            const task = doc.data();
            if (task.itemType && typeof task.itemType === 'string' && task.itemType.trim()) {
              uniqueItemTypes.add(task.itemType.trim());
            }
          });
          
          const tempItemTypes = Array.from(uniqueItemTypes).map(name => ({
            id: `temp-${name}`,
            name,
            description: "",
            active: true,
            addedDate: new Date().toISOString(),
            fromTasks: true
          })).sort((a, b) => (a.name || '').localeCompare(b.name || ''));
          
          setItemTypes(tempItemTypes);
        } catch (fallbackError) {
          console.error("Error in fallback item types fetch:", fallbackError);
        }
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, []);

  const filteredItemTypes = (itemTypes || []).filter((itemType: any) => {
    if (!itemType || !itemType.name) return false;
    const matchesSearch = (itemType.name || "").toLowerCase().includes(itemTypeSearchTerm.toLowerCase()) ||
      (itemType.description || "").toLowerCase().includes(itemTypeSearchTerm.toLowerCase());
    const matchesActive = itemTypeActiveFilter === "" ||
      (itemTypeActiveFilter === "active" && itemType.active !== false) ||
      (itemTypeActiveFilter === "inactive" && itemType.active === false);
    const matchesCategory = itemTypeCategoryFilter === "" || itemType.categoryId === itemTypeCategoryFilter;
    const matchesSubcategory = itemTypeSubcategoryFilter === "" || itemType.subcategoryId === itemTypeSubcategoryFilter;
    return matchesSearch && matchesActive && matchesCategory && matchesSubcategory;
  });

  const handleItemTypeChange = (e: any) => {
    const { name, value, type, checked } = e.target;
    setItemType((prev: any) => {
      const updated = {
        ...prev,
        [name]: type === "checkbox" ? checked : value
      };
      // If category changes, clear subcategory (it might not belong to new category)
      if (name === "categoryId" && value !== prev.categoryId) {
        updated.subcategoryId = "";
      }
      return updated;
    });
  };

  const openCreateModal = (itemTypeToEdit?: any) => {
    if (itemTypeToEdit) {
      setEditingItemType(itemTypeToEdit);
      setItemType(itemTypeToEdit);
    } else {
      setEditingItemType(null);
      setItemType(defaultItemType);
    }
    setShowCreateModal(true);
  };

  const handleSubmit = async (e: any) => {
    e.preventDefault();
    setSaving(true);

    try {
      // VALIDATION: Both categoryId and subcategoryId are required
      if (!itemType.categoryId || itemType.categoryId.trim() === '') {
        alert("Please select a category. Both category and subcategory are required.");
        setSaving(false);
        return;
      }
      
      if (!itemType.subcategoryId || itemType.subcategoryId.trim() === '') {
        alert("Please select a subcategory. Both category and subcategory are required.");
        setSaving(false);
        return;
      }
      
      const itemTypeData = {
        name: itemType.name,
        description: itemType.description || "",
        categoryId: itemType.categoryId,
        subcategoryId: itemType.subcategoryId,
        active: itemType.active !== false,
        addedDate: itemType.addedDate || new Date().toISOString(),
        updatedAt: new Date().toISOString()
      };

      // Check if this is a temporary item (from tasks) or an existing one
      const isTemporary = editingItemType?.id?.startsWith('temp-') || editingItemType?.fromTasks;
      
      try {
        if (editingItemType && !isTemporary) {
          // Update existing item type in collection
          await setDoc(
            doc(db, "itemTypes", editingItemType.id),
            itemTypeData,
            { merge: true }
          );
          setItemTypes((prev: any[]) => 
            prev.map(it => it.id === editingItemType.id ? { ...it, ...itemTypeData, fromTasks: false } : it)
          );
          alert("Item Type updated successfully!");
        } else {
          // Create new item type or convert temporary one to permanent
          const itemTypeRef = await addDoc(collection(db, "itemTypes"), itemTypeData);
          const newItemType = { ...itemTypeData, id: itemTypeRef.id, fromTasks: false };
          
          // If it was temporary, replace it; otherwise add it
          if (isTemporary) {
            setItemTypes((prev: any[]) => 
              prev.map(it => it.id === editingItemType.id ? newItemType : it)
            );
          } else {
            setItemTypes((prev: any) => [...prev, newItemType].sort((a, b) => 
              (a.name || '').localeCompare(b.name || '')
            ));
          }
          alert("Item Type saved successfully!");
        }
      } catch (saveError: any) {
        // Log the full error for debugging
        console.error("Error saving item type to Firestore:", saveError);
        console.error("Error code:", saveError.code);
        console.error("Error message:", saveError.message);
        
        // Check if it's a permission error
        if (saveError.code === 'permission-denied' || saveError.message?.includes('permission') || saveError.message?.includes('PERMISSION_DENIED')) {
          alert(`Permission denied: Unable to save item type. Please ensure:\n1. You are logged in as an admin\n2. Firestore security rules allow admin writes to itemTypes collection\n\nError: ${saveError.message}`);
        } else {
          // Other error (network, validation, etc.)
          alert(`Failed to save item type: ${saveError.message || 'Unknown error'}\n\nPlease try again or contact support if the issue persists.`);
        }
        
        // Don't update local state on error - let user retry
        setSaving(false);
        return; // Exit early, don't close modal
      }

      setShowCreateModal(false);
      setItemType(defaultItemType);
      setEditingItemType(null);
    } catch (error) {
      console.error("Error saving item type:", error);
      alert("Error saving item type. Please try again.");
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return <div className="p-8">Loading...</div>;
  }

  return (
    <div className="p-8">
      <div className="flex justify-between items-center mb-6">
        <h1 className="text-3xl font-bold">Item Types</h1>
        <button
          onClick={() => openCreateModal()}
          className="bg-[#00e3ff] text-black px-4 py-2 rounded hover:bg-[#00e3ff]/80"
        >
          + Add Item Type
        </button>
      </div>

      {/* Filters */}
      <div className="mb-4 grid grid-cols-6 gap-2">
        <input
          type="text"
          className="p-2 border border-gray-300 rounded text-sm focus:outline-none focus:ring-2 focus:ring-[#00e3ff] col-span-2"
          placeholder="Search item types by name or description..."
          value={itemTypeSearchTerm}
          onChange={(e) => setItemTypeSearchTerm(e.target.value)}
        />
        <select
          className="p-2 border border-gray-300 rounded text-sm focus:outline-none focus:ring-2 focus:ring-[#00e3ff]"
          value={itemTypeCategoryFilter}
          onChange={(e) => {
            setItemTypeCategoryFilter(e.target.value);
            setItemTypeSubcategoryFilter(""); // Reset subcategory when category changes
          }}
        >
          <option value="">All Categories</option>
          {categories.map((cat: any) => (
            <option key={cat.id} value={cat.id}>{cat.name}</option>
          ))}
        </select>
        <select
          className="p-2 border border-gray-300 rounded text-sm focus:outline-none focus:ring-2 focus:ring-[#00e3ff]"
          value={itemTypeSubcategoryFilter}
          onChange={(e) => setItemTypeSubcategoryFilter(e.target.value)}
          disabled={!itemTypeCategoryFilter}
        >
          <option value="">All Subcategories</option>
          {subcategories
            .filter((sub: any) => !itemTypeCategoryFilter || sub.categoryId === itemTypeCategoryFilter)
            .map((sub: any) => (
              <option key={sub.id} value={sub.id}>{sub.name}</option>
            ))}
        </select>
        <select
          className="p-2 border border-gray-300 rounded text-sm focus:outline-none focus:ring-2 focus:ring-[#00e3ff]"
          value={itemTypeActiveFilter}
          onChange={(e) => setItemTypeActiveFilter(e.target.value)}
        >
          <option value="">All Status</option>
          <option value="active">Active</option>
          <option value="inactive">Inactive</option>
        </select>
      </div>

      {/* Table */}
      <div className="overflow-x-auto border rounded-lg">
        <table className="min-w-full border">
          <thead>
            <tr className="bg-gray-100">
              <th className="p-2 border text-center">Name</th>
              <th className="p-2 border text-center">Category</th>
              <th className="p-2 border text-center">Subcategory</th>
              <th className="p-2 border text-center">Description</th>
              <th className="p-2 border text-center">Active</th>
              <th className="p-2 border text-center">Added Date</th>
              <th className="p-2 border text-center">Action</th>
            </tr>
          </thead>
          <tbody>
            {filteredItemTypes.map((it: any) => {
              const category = categories.find((cat: any) => cat.id === it.categoryId);
              const subcategory = subcategories.find((sub: any) => sub.id === it.subcategoryId);
              return (
                <tr key={it.id} className="text-center">
                  <td className="p-2 border">
                    {it.name}
                    {it.fromTasks && (
                      <span className="ml-2 text-xs text-gray-500">(from tasks)</span>
                    )}
                  </td>
                  <td className="p-2 border">{category ? category.name : "-"}</td>
                  <td className="p-2 border">{subcategory ? subcategory.name : "-"}</td>
                  <td className="p-2 border">{it.description || "-"}</td>
                  <td className="p-2 border">{it.active ? "Yes" : "No"}</td>
                  <td className="p-2 border">
                    {it.addedDate ? new Date(it.addedDate).toLocaleDateString() : "-"}
                  </td>
                  <td className="p-2 border">
                    <button
                      className="bg-green-500 text-white px-3 py-1 rounded hover:bg-green-600"
                      onClick={() => openCreateModal(it)}
                    >
                      {it.fromTasks ? "Save to Collection" : "Edit"}
                    </button>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
        {filteredItemTypes.length === 0 && (
          <div className="text-center py-8 text-gray-500">
            No item types found.
          </div>
        )}
      </div>

      {/* Create/Edit Modal */}
      {showCreateModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white p-6 rounded-lg max-w-2xl w-full max-h-[90vh] overflow-y-auto">
            <div className="flex justify-between items-center mb-4">
              <h2 className="text-2xl font-bold">
                {editingItemType ? "Edit Item Type" : "Create Item Type"}
              </h2>
              <button
                onClick={() => {
                  setShowCreateModal(false);
                  setItemType(defaultItemType);
                  setEditingItemType(null);
                }}
                className="text-gray-500 hover:text-gray-700 text-2xl"
              >
                &times;
              </button>
            </div>

            <form onSubmit={handleSubmit}>
              <div className="mb-4">
                <label className="block text-sm font-medium mb-1">Name *</label>
                <input
                  type="text"
                  name="name"
                  value={itemType.name}
                  onChange={handleItemTypeChange}
                  required
                  className="w-full p-2 border border-gray-300 rounded"
                />
              </div>

              <div className="mb-4">
                <label className="block text-sm font-medium mb-1">Category *</label>
                <select
                  name="categoryId"
                  value={itemType.categoryId || ""}
                  onChange={handleItemTypeChange}
                  required
                  className="w-full p-2 border border-gray-300 rounded"
                >
                  <option value="">Select a category</option>
                  {categories.map((cat: any) => (
                    <option key={cat.id} value={cat.id}>{cat.name}</option>
                  ))}
                </select>
              </div>

              <div className="mb-4">
                <label className="block text-sm font-medium mb-1">Subcategory *</label>
                <select
                  name="subcategoryId"
                  value={itemType.subcategoryId || ""}
                  onChange={handleItemTypeChange}
                  required
                  className="w-full p-2 border border-gray-300 rounded"
                  disabled={!itemType.categoryId}
                >
                  <option value="">Select a subcategory</option>
                  {itemType.categoryId ? (
                    (() => {
                      // Get the selected category to verify
                      const selectedCategory = categories.find((cat: any) => cat.id === itemType.categoryId);
                      
                      // Filter subcategories that belong to this category
                      const filteredSubs = subcategories.filter((sub: any) => {
                        if (!sub || !sub.categoryId) return false;
                        // Compare as strings to handle any type mismatches
                        const subCategoryId = String(sub.categoryId || '').trim();
                        const selectedCategoryId = String(itemType.categoryId || '').trim();
                        return subCategoryId === selectedCategoryId;
                      });
                      
                      // Debug logging
                      if (filteredSubs.length === 0) {
                        console.warn(`No subcategories found for category ID: ${itemType.categoryId}`, {
                          selectedCategory: selectedCategory?.name,
                          selectedCategoryId: itemType.categoryId,
                          allSubcategories: subcategories.map((s: any) => ({ 
                            id: s.id, 
                            name: s.name, 
                            categoryId: s.categoryId,
                            categoryIdType: typeof s.categoryId
                          })),
                          subcategoriesForThisCategory: subcategories.filter((s: any) => 
                            String(s.categoryId || '') === String(itemType.categoryId || '')
                          )
                        });
                      } else {
                        console.log(`Found ${filteredSubs.length} subcategories for category "${selectedCategory?.name}" (ID: ${itemType.categoryId}):`, 
                          filteredSubs.map((s: any) => s.name)
                        );
                      }
                      
                      return filteredSubs.map((sub: any) => (
                        <option key={sub.id} value={sub.id}>{sub.name}</option>
                      ));
                    })()
                  ) : (
                    <option value="" disabled>Select a category first</option>
                  )}
                </select>
              </div>

              <div className="mb-4">
                <label className="block text-sm font-medium mb-1">Description</label>
                <textarea
                  name="description"
                  value={itemType.description || ""}
                  onChange={handleItemTypeChange}
                  rows={3}
                  className="w-full p-2 border border-gray-300 rounded"
                />
              </div>

              <div className="mb-4">
                <label className="flex items-center">
                  <input
                    type="checkbox"
                    name="active"
                    checked={itemType.active}
                    onChange={handleItemTypeChange}
                    className="mr-2"
                  />
                  <span>Active</span>
                </label>
              </div>

              <div className="flex justify-end gap-2">
                <button
                  type="button"
                  onClick={() => {
                    setShowCreateModal(false);
                    setItemType(defaultItemType);
                    setEditingItemType(null);
                  }}
                  className="px-4 py-2 border border-gray-300 rounded hover:bg-gray-100"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={saving}
                  className="px-4 py-2 bg-[#00e3ff] text-black rounded hover:bg-[#00e3ff]/80 disabled:opacity-50"
                >
                  {saving ? "Saving..." : editingItemType ? "Update" : "Create"}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}

